import { useEffect, useId, useState } from 'react';
import { Badge, Button, Field, FilterChips, Icon, Input, SectionCard, Toggle, type ChipOption } from '../../../components/ui';
import { formatPercentFromBps } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { LIMITE_AUTOPAUSA_BPS, RITMO_MAXIMO, RITMO_MINIMO, sugerirNome, type FormularioEnvio } from '../formulario';
import type { MessageTemplateSummary, Segment, SegmentFamily, SellerOption, SenderOption } from '../types';
import { SEGMENT_FAMILY_LABEL, TEMPLATE_APPROVAL_LABEL, TEMPLATE_APPROVAL_TONE, TEMPLATE_CATEGORY_LABEL, TEMPLATE_CATEGORY_TONE } from '../vocabulario';
import { AtribuirAoTime } from './AtribuirAoTime';
import { SeletorDePublico } from './SeletorDePublico';

/**
 * Coluna esquerda do novo envio: modelo, publico, nome, atribuicao e as
 * configuracoes avancadas (ritmo, janela, travas).
 *
 * Decisoes nao obvias:
 *  1. "Modelo de mensagem" nao e um Select: a lista precisa de busca,
 *     filtros e previa do texto, entao o controle rotulado e o BOTAO que abre
 *     o modal, e a escolha atual fica num resumo ao lado (nome + selos + se
 *     combina com o publico).
 *  2. Ritmo tem atalhos (15, 20, 30, 40 por minuto) alem do campo livre: a
 *     equipe escolhe pelo risco, nao pela conta.
 *  3. "Respeitar opt-out" e um Toggle ligado e TRAVADO: existe para a pessoa
 *     ver que a regra esta la, nao para desliga-la. A trava de opt-out alto
 *     pode ser desligada, mas vem ligada.
 *  4. O campo de ritmo guarda o TEXTO digitado e so limita (5 a 120) ao sair
 *     do campo: limitar a cada tecla transformaria o "3" de "30" em 5, e
 *     ninguem conseguiria digitar 30. Valor valido no meio da digitacao ja
 *     atualiza a estimativa. O chip "Outro" leva o foco para o campo.
 */

type RitmoChip = '15' | '20' | '30' | '40' | 'outro';

const RITMOS: readonly ChipOption<RitmoChip>[] = [
  { value: '15', label: '15/min · seguro' },
  { value: '20', label: '20/min · padrão' },
  { value: '30', label: '30/min' },
  { value: '40', label: '40/min · rápido' },
  { value: 'outro', label: 'Outro' },
];

function limitarRitmo(valor: number): number {
  if (!Number.isFinite(valor)) {
    return RITMO_MINIMO;
  }
  return Math.max(RITMO_MINIMO, Math.min(RITMO_MAXIMO, Math.round(valor)));
}

function chipDoRitmo(porMinuto: number): RitmoChip {
  return porMinuto === 15 ? '15' : porMinuto === 20 ? '20' : porMinuto === 30 ? '30' : porMinuto === 40 ? '40' : 'outro';
}

function CampoRitmo({ id, describedBy, valor, onValor }: { readonly id: string; readonly describedBy: string | undefined; readonly valor: number; readonly onValor: (v: number) => void }): JSX.Element {
  const [texto, setTexto] = useState(String(valor));
  // Chip escolhido (ou valor limitado no blur) reescreve o campo.
  useEffect(() => {
    setTexto(String(valor));
  }, [valor]);
  return (
    <Input
      id={id}
      aria-describedby={describedBy}
      type="number"
      inputMode="numeric"
      min={RITMO_MINIMO}
      max={RITMO_MAXIMO}
      step={5}
      value={texto}
      onChange={(e) => {
        const bruto = e.target.value;
        setTexto(bruto);
        const n = Number(bruto);
        if (bruto.trim() !== '' && Number.isInteger(n) && n >= RITMO_MINIMO && n <= RITMO_MAXIMO) {
          onValor(n);
        }
      }}
      onBlur={() => {
        const limitado = limitarRitmo(texto.trim() === '' ? valor : Number(texto));
        setTexto(String(limitado));
        onValor(limitado);
      }}
      className="sm:max-w-[12rem]"
    />
  );
}

function familiasDoPublico(form: FormularioEnvio, segments: readonly Segment[]): readonly SegmentFamily[] {
  return Array.from(new Set(segments.filter((s) => form.segmentos.includes(s.key)).map((s) => s.family)));
}

export function FormularioNovoEnvio({
  form,
  onChange,
  template,
  segments,
  sellers,
  company,
  sender,
  onEscolherModelo,
  className = '',
}: {
  readonly form: FormularioEnvio;
  readonly onChange: (patch: Partial<FormularioEnvio>) => void;
  readonly template: MessageTemplateSummary | null;
  readonly segments: readonly Segment[];
  readonly sellers: readonly SellerOption[];
  readonly company: CompanySummary;
  readonly sender: SenderOption | null;
  readonly onEscolherModelo: () => void;
  readonly className?: string;
}): JSX.Element {
  const resumoId = useId();
  const avancadoId = useId();
  const ritmoId = useId();
  const familias = form.fonte === 'segmentos' ? familiasDoPublico(form, segments) : [];
  const combina = template !== null && familias.length > 0 && familias.every((f) => template.fits.includes(f));
  const naoCombina = template !== null && familias.length > 0 && !combina;

  return (
    <SectionCard
      id="novo-envio"
      icon="disparo"
      title="Novo envio em massa"
      description="Modelo, público pós-fato e quem atende as respostas. Pela API oficial só sai modelo aprovado (regra da Meta); pela Evolution também sai texto livre."
      className={className}
    >
      <div className="flex flex-col gap-6">
        <Field label="Modelo de mensagem" required hint="O texto que o contato vai receber. Utility para avisos sobre algo que a pessoa começou; marketing para retomadas com oferta.">
          {({ id, describedBy }) => (
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <div id={resumoId} aria-live="polite" className="flex min-h-11 flex-1 flex-wrap items-center gap-2 rounded-lg border border-line bg-elevated px-3 py-2 text-sm">
                  {template === null ? (
                    <span className="text-ink-subtle">Nenhum modelo escolhido</span>
                  ) : (
                    <>
                      <span className="min-w-0 truncate font-medium text-ink">{template.name}</span>
                      <Badge tone={TEMPLATE_CATEGORY_TONE[template.category]}>{TEMPLATE_CATEGORY_LABEL[template.category]}</Badge>
                      <Badge tone={TEMPLATE_APPROVAL_TONE[template.approval]} dot>
                        {TEMPLATE_APPROVAL_LABEL[template.approval]}
                      </Badge>
                      {combina ? (
                        <Badge tone="good" icon="check">
                          Combina com o público
                        </Badge>
                      ) : null}
                    </>
                  )}
                </div>
                <Button id={id} variant="secondary" size="lg" icon="modelo" onClick={onEscolherModelo} aria-describedby={[resumoId, describedBy].filter((v): v is string => v !== undefined).join(' ')}>
                  {template === null ? 'Escolher modelo' : 'Trocar modelo'}
                </Button>
              </div>
              {naoCombina && template !== null ? (
                <p className="flex items-start gap-1.5 text-xs text-status-warning">
                  <Icon name="alerta" size="xs" className="mt-0.5" />
                  Este texto foi escrito para {template.fits.map((f) => SEGMENT_FAMILY_LABEL[f].toLowerCase()).join(' e ')}; o público marcado inclui{' '}
                  {familias
                    .filter((f) => !template.fits.includes(f))
                    .map((f) => SEGMENT_FAMILY_LABEL[f].toLowerCase())
                    .join(' e ')}
                  . A mensagem pode não fazer sentido para parte dele.
                </p>
              ) : null}
            </div>
          )}
        </Field>

        <SeletorDePublico form={form} segments={segments} temFunilExpirados={company.funnels.includes('expirados')} onChange={onChange} />

        <Field label="Nome do envio" required hint="Só a equipe vê. Ajuda a achar o envio na lista e no relatório depois.">
          {({ id, describedBy }) => (
            <div className="flex gap-2">
              <Input id={id} aria-describedby={describedBy} placeholder="Ex.: Retomada do comercial · setembro" value={form.nome} onChange={(e) => onChange({ nome: e.target.value })} maxLength={80} />
              <Button variant="secondary" size="lg" icon="raio" onClick={() => onChange({ nome: sugerirNome(form, new Date()) })} title="Preencher com o público e a data de hoje">
                Sugerir
              </Button>
            </div>
          )}
        </Field>

        <AtribuirAoTime sellers={sellers} selecionadas={form.vendedoras} onChange={(vendedoras) => onChange({ vendedoras })} companyName={company.name} companyId={company.id} sender={sender} />

        <div className="rounded-xl border border-line">
          <button
            type="button"
            aria-expanded={form.avancadoAberto}
            aria-controls={avancadoId}
            onClick={() => onChange({ avancadoAberto: !form.avancadoAberto })}
            className="flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium text-ink transition-colors hover:text-brand"
          >
            <span className="flex items-center gap-2">
              <Icon name="config" size="sm" />
              Configurações avançadas
              <span className="font-normal text-ink-subtle">
                · {form.porMinuto}/min · {form.janelaInicio} às {form.janelaFim}
              </span>
            </span>
            <Icon name="chevron" size="sm" className={`transition-transform ${form.avancadoAberto ? 'rotate-180' : ''}`} />
          </button>
          {form.avancadoAberto ? (
            <div id={avancadoId} className="flex flex-col gap-5 border-t border-line px-4 py-4">
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-ink">Ritmo</p>
                <FilterChips
                  options={RITMOS}
                  value={chipDoRitmo(form.porMinuto)}
                  onChange={(v) => {
                    if (v === 'outro') {
                      document.getElementById(ritmoId)?.focus();
                    } else {
                      onChange({ porMinuto: Number(v) });
                    }
                  }}
                  label="Ritmo do envio"
                />
                <Field
                  label="Mensagens por minuto"
                  htmlFor={ritmoId}
                  hint={`De ${RITMO_MINIMO} a ${RITMO_MAXIMO}. Num número comum, até 20 por minuto parece conversa; acima de 40 parece robô.`}
                >
                  {({ id, describedBy }) => <CampoRitmo id={id} describedBy={describedBy} valor={form.porMinuto} onValor={(porMinuto) => onChange({ porMinuto })} />}
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:max-w-md">
                <Field label="Enviar a partir de" hint="Horário da empresa.">
                  {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} type="time" value={form.janelaInicio} onChange={(e) => onChange({ janelaInicio: e.target.value })} />}
                </Field>
                <Field label="Até">{({ id }) => <Input id={id} type="time" value={form.janelaFim} onChange={(e) => onChange({ janelaFim: e.target.value })} />}</Field>
              </div>
              <Toggle
                checked={form.pausarSeOptOutAlto}
                onChange={(v) => onChange({ pausarSeOptOutAlto: v })}
                label={`Pausar sozinho se o opt-out passar de ${formatPercentFromBps(LIMITE_AUTOPAUSA_BPS)}`}
                description="Mede depois das primeiras 100 mensagens. Protege o número do comercial, que é da empresa: bloqueado, a régua inteira para."
              />
              <Toggle checked disabled onChange={() => undefined} label="Respeitar opt-out" description="Obrigatório: quem pediu para sair nunca recebe, em nenhum envio." />
            </div>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
}
